// Peak agreement export — the AP "Peak Period Forecasting Agreement" written back
// out as the .xlsx the Business Partner expects to receive, plus a second sheet
// carrying the working. Kept out of the page so the sheet can be built and checked
// without a browser (see scripts under the peak planning notes in CLAUDE.md).
//
// buildAgreementWorkbook(plan, forecast) → an XLSX workbook object (testable)
// exportAgreement(plan, forecast)        → the same workbook, downloaded
import * as XLSX from 'xlsx';
import { saturdays, sundays } from './peakPlan';

export function buildAgreementWorkbook(plan, f) {
  if (!f) throw new Error('No forecast to export');
  const year = Number(plan.year);
  const rows = [];
  const put = (r, c, v) => { (rows[r] || (rows[r] = []))[c] = v; };

  put(0, 1, 'Peak Period Forecasting Agreement');
  put(2, 1, 'Mail Service Number'); put(2, 2, plan.mail_service_number);
  put(3, 1, 'Mail Service Name'); put(3, 2, plan.mail_service_name);
  put(4, 1, 'PDC/DC Facility'); put(4, 2, plan.facility);

  put(6, 2, `${f.base ? f.base.label : plan.base_month}\nBase Data`);
  put(6, 3, `November ${year} Forecasted`); put(6, 6, `December ${year} Forecasted`);
  put(7, 1, 'Daily Volumes'); put(7, 2, f.base ? Math.round(f.base.daily) : ''); put(7, 3, Math.round(f.nov.daily)); put(7, 6, Math.round(f.dec.daily));
  put(8, 1, 'Total Monthly Forecasted Volumes'); put(8, 3, Math.round(f.nov.monthly)); put(8, 6, Math.round(f.dec.monthly));
  put(10, 1, 'Daily Drivers (logins)'); put(10, 2, f.base ? Number(f.base.drivers.toFixed(1)) : ''); put(10, 3, Number(f.nov.driversNeeded.toFixed(1))); put(10, 6, Number(f.dec.driversNeeded.toFixed(1)));
  put(11, 1, 'Variance in Drivers (to base)');
  put(11, 3, f.base ? Number((f.nov.driversNeeded - f.base.drivers).toFixed(1)) : '');
  put(11, 6, f.base ? Number((f.dec.driversNeeded - f.base.drivers).toFixed(1)) : '');
  put(12, 1, 'Average Articles per Driver'); put(12, 2, f.base ? Number(f.base.articlesPerDriver.toFixed(1)) : ''); put(12, 3, Number(plan.articles_per_driver)); put(12, 6, Number(plan.articles_per_driver));
  put(14, 1, 'PEAK Variance to Last Year'); put(14, 2, `${(f.growthVsLast || 0).toFixed(2)}%`);

  put(16, 3, 'Standard Drivers'); put(16, 6, 'XL Drivers');
  put(17, 1, 'No of existing BAU Drivers'); put(17, 3, plan.bau_drivers); put(17, 6, plan.bau_xl);
  put(18, 1, 'No of additional Peak Drivers Planned'); put(18, 3, plan.extra_drivers); put(18, 6, plan.extra_xl);
  put(20, 3, 'November Planned'); put(20, 6, 'December Planned');
  put(21, 1, 'Total Drivers for Peak'); put(21, 3, f.drivers.total); put(21, 6, f.drivers.total);
  put(22, 1, 'Total Volume Capacity'); put(22, 3, Math.round(f.capacity.nov)); put(22, 6, Math.round(f.capacity.dec));
  put(23, 1, 'Variance to Forecast'); put(23, 3, Math.round(f.capacity.novVariance)); put(23, 6, Math.round(f.capacity.decVariance));

  put(25, 1, 'Subsidy Summary');
  put(26, 3, 'Regular van subsidy'); put(26, 6, 'XL van subsidy');
  put(27, 1, 'No of PVA supported Peak Vans'); put(27, 3, plan.subsidy?.vans || 0); put(27, 6, plan.subsidy?.xl_vans || 0);
  put(28, 1, 'Proposed rate for subsidy'); put(28, 3, plan.subsidy?.rate || 0); put(28, 6, plan.subsidy?.xl_rate || 0);
  put(29, 1, 'Additional Driver Comments'); put(29, 3, plan.subsidy?.comments || '');

  put(31, 1, 'Weekend PDC Deliveries Planned');
  put(32, 3, 'Date'); put(32, 4, 'Operating'); put(32, 5, 'No of Drivers');
  put(32, 6, 'Date'); put(32, 7, 'Operating'); put(32, 8, 'No of Drivers');
  const wk = plan.weekend || {};
  const emit = (isos, startRow, colDate) => isos.forEach((iso, i) => {
    const w = wk[iso] || {};
    put(startRow + i, colDate, iso);
    put(startRow + i, colDate + 1, w.operating ? 'Yes' : 'No');
    put(startRow + i, colDate + 2, w.operating ? (w.drivers || 0) : 0);
  });
  put(33, 1, 'Saturday Operations');
  emit(saturdays(year, 10), 33, 3);
  emit(saturdays(year, 11), 33, 6);
  put(39, 1, 'Sunday Operations');
  emit(sundays(year, 10), 39, 3);
  emit(sundays(year, 11), 39, 6);

  put(45, 1, 'Comments');
  put(46, 1, 'Staging Reload Options/ XL and Van subsidy requirements'); put(46, 2, plan.comments?.staging || '');
  put(47, 1, 'Indoor Sorting (if applicable)'); put(47, 2, plan.comments?.sorting || '');
  put(48, 1, 'Express Post Arrangements (if applicable)'); put(48, 2, plan.comments?.express || '');
  put(49, 1, 'Opportunities to Create Capacity'); put(49, 2, plan.comments?.capacity || '');

  const ws = XLSX.utils.aoa_to_sheet(rows.map((r) => r || []));
  ws['!cols'] = [{ wch: 3 }, { wch: 42 }, { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }];

  // Sheet 2 — the working.
  const w = [
    ['Peak plan working', `${plan.mail_service_name} · ${plan.facility}`],
    [],
    ['Basis', plan.basis === 'ap' ? 'June base × AP uplift factors' : `Last peak actual + ${plan.growth_pct}%`],
    ['Base month', f.base ? f.base.label : plan.base_month, f.base ? Math.round(f.base.daily) : '', 'parcels per weekday (actual)'],
    ['Last peak actual', `Peak ${year - 1}`, Math.round(f.last.total), `${f.last.operatingDays} operating days`],
    ['Target for this season', f.target ? Math.round(f.target) : '', '', 'last peak actual × growth'],
    ['This plan carries', Math.round(f.total), '', `difference ${Math.round(f.targetGap || 0)}`],
    [],
    ['Month', 'Weekday rate', 'Weekdays', 'Weekend days', 'Monthly volume', 'Drivers needed'],
    [`November ${year}`, Math.round(f.nov.daily), f.nov.weekdayDays, f.nov.weekendDays, Math.round(f.nov.monthly), Number(f.nov.driversNeeded.toFixed(1))],
    [`December ${year}`, Math.round(f.dec.daily), f.dec.weekdayDays, f.dec.weekendDays, Math.round(f.dec.monthly), Number(f.dec.driversNeeded.toFixed(1))],
    [],
    ['Capacity = drivers × articles per driver × operating days'],
    ['Total drivers', f.drivers.total, 'Articles per driver', Number(plan.articles_per_driver)],
    [],
    ['Busiest day last season', f.peakDay ? f.peakDay.was.iso : '', f.peakDay ? f.peakDay.was.parcels : '', f.peakDay ? `${f.peakDay.was.drivers} logins` : '', f.peakDay ? `${Math.round(f.peakDay.was.perDriver)} articles each` : ''],
    ['Same day grown', f.peakDay ? Math.round(f.peakDay.parcels) : '', f.peakDay ? Number(f.peakDay.driversNeeded.toFixed(1)) : '', 'drivers at planning rate', f.peakDay && f.peakDay.driversAtLastRate ? Number(f.peakDay.driversAtLastRate.toFixed(1)) : '', 'at last season rate'],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(w);
  ws2['!cols'] = [{ wch: 30 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 16 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Peak ${year}`);
  XLSX.utils.book_append_sheet(wb, ws2, 'Working');
  return wb;
}

export function exportAgreement(plan, f) {
  const wb = buildAgreementWorkbook(plan, f);
  const name = `Peak Period Forecasting Agreement ${plan.year} - ${plan.mail_service_name || 'City Contract'}.xlsx`;
  XLSX.writeFile(wb, name);
  return name;
}
